import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons';
import { Controller, useFormContext } from 'FrontRoyalReactHookForm';
import useLocalizedCountries from 'Countries/countriesUtils';
import states from 'states';
import { type CountryCode } from 'libphonenumber-js';
import { Input } from 'Input';
import { PhoneInput } from 'PhoneInput';
import { Autocomplete } from 'Autocomplete';
import { InlineLink } from 'InlineLink';
import { US_COUNTRY_CODE } from '../constants';
import { type MailingAddressFormValues } from '../editDocuments.types';
import { LabeledField } from './LabeledField';

// The address fields themselves. Split out of MailingAddressSection so the section keeps to the
// form/save concerns and this keeps to the layout.
export function MailingAddressFields({ initialShowAddressLine2 }: { initialShowAddressLine2: boolean }) {
    const { t } = useTranslation('back_royal');
    const { control, formState, watch } = useFormContext<MailingAddressFormValues>();
    const { errors } = formState;
    const country = watch('country');

    const [showAddressLine2, setShowAddressLine2] = useState(initialShowAddressLine2);

    const countryOptions = useLocalizedCountries()
        .filter(option => option.text.length > 0)
        .map(option => ({ value: option.value, label: option.text }));

    const requiredMessage = t('settings.edit_documents.field_required');
    const addressLine1Error = errors.address_line_1 ? t(addressLine1ErrorKey(errors.address_line_1.type)) : '';
    // main requires city, state and zip only for US addresses; country, address line 1 and phone
    // are always required.
    const isUsAddress = country === US_COUNTRY_CODE;

    return (
        <>
            <LabeledField
                label={t('settings.edit_documents.country')}
                isRequired
                errorMessage={errors.country ? requiredMessage : undefined}
            >
                <Controller
                    control={control}
                    name="country"
                    render={({ field }) => (
                        <Autocomplete
                            options={countryOptions}
                            value={field.value}
                            onValueChange={field.onChange}
                            aria-label={t('settings.edit_documents.country')}
                            placeholder={t('settings.edit_documents.country')}
                            isRequired
                            className="w-full"
                        />
                    )}
                />
            </LabeledField>

            <Input
                name="address_line_1"
                isRequired
                label={t('settings.edit_documents.address_line_1')}
                placeholder={t('settings.edit_documents.address_line_1_placeholder')}
                isInvalid={!!errors.address_line_1}
                errorMessage={addressLine1Error}
            />

            {showAddressLine2 ? (
                <Input
                    name="address_line_2"
                    label={t('settings.edit_documents.address_line_2')}
                    placeholder={t('settings.edit_documents.address_line_2_placeholder')}
                    isInvalid={!!errors.address_line_2}
                    errorMessage={
                        errors.address_line_2 ? t('settings.edit_documents.address_line_2_error_maxlength') : ''
                    }
                />
            ) : (
                <div>
                    <InlineLink className="text-sm" onPress={() => setShowAddressLine2(true)}>
                        <FontAwesomeIcon icon={faPlusCircle} className="me-2" />
                        {t('settings.edit_documents.add_address_line_2')}
                    </InlineLink>
                </div>
            )}

            <Input
                name="city"
                isRequired={isUsAddress}
                label={t('settings.edit_documents.city')}
                isInvalid={!!errors.city}
                errorMessage={errors.city ? requiredMessage : ''}
            />

            {/* State and zip share a row: both hold short values, so full-width inputs look
                oversized next to them. They stack again below `sm`, where the container is too
                narrow to fit a state name like "District of Columbia" beside a zip. State takes the
                larger share for the same reason.

                `sm:items-end` is what lines the two controls up. The columns have different natural
                heights — state is an external label above a `Select`, while zip is an `Input` with a
                floating label inside it — and the default `align-items: stretch` would stretch the
                zip column to match without stretching the input in it, leaving a gap below the
                input. Aligning to the bottom puts the two control boxes on the same line. */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                <StateField
                    isUs={isUsAddress}
                    errorMessage={errors.state ? requiredMessage : undefined}
                    className="sm:flex-[3]"
                />

                <div className="sm:flex-[2]">
                    <Input
                        name="zip"
                        isRequired={isUsAddress}
                        label={t('settings.edit_documents.zip')}
                        isInvalid={!!errors.zip}
                        errorMessage={errors.zip ? requiredMessage : ''}
                    />
                </div>
            </div>

            <PhoneInput
                name="phone"
                isRequired
                label={t('settings.edit_documents.phone')}
                defaultCountryCode={(country || US_COUNTRY_CODE) as CountryCode}
                errorMessage={errors.phone ? requiredMessage : undefined}
            />
        </>
    );
}

// Yup reports the length rule as `max`; anything else on this field is the required rule.
function addressLine1ErrorKey(errorType?: string) {
    return errorType === 'max'
        ? 'settings.edit_documents.address_line_1_error_maxlength'
        : 'settings.edit_documents.field_required';
}

// Only the US has a fixed list of states/territories; everywhere else takes free text.
function StateField({ isUs, errorMessage, className }: { isUs: boolean; errorMessage?: string; className?: string }) {
    const { t } = useTranslation('back_royal');
    const { control } = useFormContext<MailingAddressFormValues>();

    if (!isUs) {
        return (
            <div className={className}>
                <Input name="state" label={t('settings.edit_documents.state')} />
            </div>
        );
    }

    const stateOptions = (states as { value: string; label: string }[]).map(option => ({
        value: option.value,
        label: option.label,
    }));

    return (
        <LabeledField
            label={t('settings.edit_documents.state')}
            isRequired
            errorMessage={errorMessage}
            className={className}
        >
            <Controller
                control={control}
                name="state"
                render={({ field }) => (
                    <Autocomplete
                        options={stateOptions}
                        value={field.value}
                        onValueChange={field.onChange}
                        aria-label={t('settings.edit_documents.state')}
                        placeholder={t('settings.edit_documents.state')}
                        isRequired
                        className="w-full"
                    />
                )}
            />
        </LabeledField>
    );
}
